import { useCallback } from 'react';

import { LogOutIcon, UserIcon, MoonIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { StyledBadge } from '@/components/atoms/StyledBadge/StyledBadge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { useInvitations } from '@/hooks/useInvitations';
import { usePresence } from '@/hooks/usePresence';
import useUser from '@/hooks/useUser';
import { selectAgentById } from '@/redux/presence/selectors';
import { trackEvent } from '@/segment/segment';
import { PresenceStatus } from '@/shared/types/presence';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

export const UserMenu = () => {
  // Custom hooks
  const { t } = useTranslation();
  const { hasInvitations } = useInvitations();
  const { user } = useUser();
  const navigate = useNavigate();
  const { updateAgent } = usePresence();

  // Redux
  const agent = useSelector((state: RootState) => selectAgentById(state, user?.user_id));

  // Handlers
  const handleClick = useCallback(() => {
    trackEvent(EventName.OpenUserMenu);
  }, []);

  const handleLogout = useCallback(() => {
    navigate('/logout');
  }, [navigate]);

  const profilePath = '/profile/information';

  const toggleAwayStatus = useCallback(() => {
    if (!agent) {
      return;
    }

    const updatedStatus =
      agent.status === PresenceStatus.ONLINE ? PresenceStatus.AWAY : PresenceStatus.ONLINE;

    updateAgent({ agent_id: agent.agent_id, status: updatedStatus });
  }, [agent, updateAgent]);

  const handleAwayToggleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      toggleAwayStatus();
    },
    [toggleAwayStatus]
  );

  const showMenu = !!user;

  if (!showMenu) {
    return null;
  }

  return (
    <DropdownMenu>
      <InfoTooltip content={t('pages.profile')} delayDuration={200}>
        <DropdownMenuTrigger asChild>
          <Button
            id="profile-button"
            aria-label={t('main_navigation.user_menu')}
            className="h-8 w-8 rounded hover:bg-white/16"
            variant="ghost-neutral"
            size="icon-md"
            onClick={handleClick}
          >
            <Avatar altText={user?.name || ''} userId={user?.user_id} hideTooltip />
          </Button>
        </DropdownMenuTrigger>
      </InfoTooltip>
      <DropdownMenuContent
        className="w-56"
        align="end"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="p-0">
          <div className="flex items-center gap-3 p-2">
            <StyledBadge
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              status={agent?.status}
            >
              <img src={user?.avatar} alt={user?.name} className="h-8 w-8 rounded-lg bg-sidebar" />
            </StyledBadge>
            <div className="flex min-w-0 flex-1 flex-col">
              <div
                className="mb-0.5 truncate text-sm leading-none font-semibold text-foreground"
                title={user.name}
              >
                {user.name}
              </div>
              <div
                className="truncate text-xs leading-4 font-normal text-foreground"
                title={user.email}
              >
                {user.email}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link
            to={profilePath}
            className="flex items-center gap-2 px-3 py-2 text-inherit no-underline"
          >
            <UserIcon size={16} className="text-foreground-muted" />
            <span className="text-sm">{t('profile.your_profile')}</span>
            <StyledBadge status="away" invisible={!hasInvitations} />
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="flex items-center justify-between px-3 py-2"
          onClick={handleAwayToggleClick}
        >
          <div className="flex items-center gap-2">
            <MoonIcon size={16} className="text-foreground-muted" />
            <span className="text-sm">
              <Trans
                i18nKey="profile.set_as_away"
                components={[<span key="bold" className="font-bold" />]}
              />
            </span>
          </div>
          <Switch
            name="away-status"
            checked={agent?.status === PresenceStatus.AWAY}
            className="ml-auto"
          />
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 px-3 py-2">
          <LogOutIcon size={16} className="text-foreground-muted" />
          <span className="text-sm">{t('invitations.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
